/**
 * Specialty adapter for the web application.
 *
 * rev v11.0.0 · Phase 2 / C1
 *
 * The Specialty Registry is the single source of truth. This module is the
 * thin bridge between it and the React layer: it resolves identifiers, it
 * computes the theme, and it knows which specialties have a catalogue.
 *
 * It contains no state. State lives in `useSpecialty()`.
 */
import {
  SPECIALTIES,
  SPECIALTY_SLUGS,
  specialtyRegistry,
  resolveSpecialtyId,
  type SpecialtyDefinition,
  type ThemeTokens,
} from "@dikdoc/specialty-registry";

export { SPECIALTIES, SPECIALTY_SLUGS, resolveSpecialtyId };
export type { SpecialtyDefinition, ThemeTokens };

/** Every specialty, in clinical order. */
export const ALL_SPECIALTIES: readonly SpecialtyDefinition[] = SPECIALTIES;

/** The specialties that have acts in the NGAP Maroc catalogue. */
export const CATALOGUE_SPECIALTIES: readonly SpecialtyDefinition[] = SPECIALTIES.filter(
  (s) => s.ngap.catalogueActs > 0,
);

/**
 * The specialties whose new experience can be enabled.
 *
 * The seven workspaces that have no catalogue act of their own are still
 * real clinical workspaces (cardiologie, pediatrie …), so they are not
 * excluded — but their cotations are billed inside another scope, which is
 * what `catalogueActs: 0` records.
 */
export const ROLLOUT_SPECIALTIES: readonly SpecialtyDefinition[] = SPECIALTIES;

/** Resolve any identifier — slug or legacy code — to a manifest. */
export function getSpecialty(id: string | null | undefined): SpecialtyDefinition | null {
  return specialtyRegistry.get(id);
}

/** The theme of a specialty, as CSS custom properties. */
export function themeStyleFor(
  id: string | null | undefined,
): Record<string, string> {
  return specialtyRegistry.themeStyle(id);
}

/** The selector that scopes a specialty in the DOM. */
export function scopeSelectorFor(
  id: string | null | undefined,
): string {
  return specialtyRegistry.scopeSelector(id);
}

/** The feature-flag key of a specialty. */
export function flagKeyFor(id: string | null | undefined): string | null {
  return specialtyRegistry.featureFlag(id);
}

/**
 * The specialties a physician can work in, from their specialisations.
 *
 * The doctor's record carries the human-readable specialisation, so the
 * matching is done on the manifest's `doctorSpecs`.
 */
export function specialtiesForDoctorSpecs(
  specs: readonly string[],
): SpecialtyDefinition[] {
  return specialtyRegistry.forDoctorSpecs(specs);
}

/**
 * The specialties that offer a module requiring a given capability.
 *
 * Used by the capability picker: choosing "ECG" shows which workspaces can
 * actually use it.
 */
export function specialtiesWithCapability(capability: string): SpecialtyDefinition[] {
  return SPECIALTIES.filter((s) =>
    s.navigation.some((m) => m.requires.includes(capability)),
  );
}

/** Every capability referenced by the registry, sorted. */
export const ALL_CAPABILITIES: readonly string[] = [
  ...new Set(
    SPECIALTIES.flatMap((s) => [
      ...s.navigation.flatMap((m) => m.requires),
      ...s.panels.flatMap((p) => p.requires),
    ]),
  ),
].sort();

/**
 * The application screen that carries each declared clinical module.
 *
 * rev v11.1 · AGENT 04
 *
 * WHY THIS TABLE EXISTS
 * ---------------------
 * A manifest declares WHICH module belongs to a workspace; it does not
 * decide WHERE the module is implemented. That is the web layer's job —
 * exactly what `PANEL_ROUTES` does for the panels, in
 * `apps/web/src/routes/clinical/$specialty.tsx`.
 *
 * The distinction matters because the application has NO `/clinical/<module>`
 * screens: the only route under `/clinical` is `/clinical/$specialty`. A
 * navigation module that pointed at `/clinical/consult` was therefore a dead
 * link — the router matched it against `$specialty`, the specialty could not
 * be resolved, and the clinician was silently sent back to the home page.
 *
 * A module is listed here ONLY when the application really has the screen
 * that carries it. No screen is invented to fill a gap.
 */
export const MODULE_ROUTES: Readonly<
  Record<string, { to: string; label: string }>
> = {
  "/clinical/ai": { to: "/ai", label: "Suggestions d'IA" },
  "/clinical/assessment": { to: "/consultation", label: "Évaluation clinique" },
  "/clinical/billing": { to: "/billing", label: "Cotations" },
  "/clinical/consult": { to: "/consultation", label: "Consultation" },
  "/clinical/glycemia": { to: "/lab-results", label: "Glycémie" },
  "/clinical/imaging": { to: "/radiology", label: "Imagerie" },
  "/clinical/lab": { to: "/lab-results", label: "Résultats de laboratoire" },
  "/clinical/neuro": { to: "/consultation", label: "Examen neurologique" },
  "/clinical/patients": { to: "/patients", label: "Patients" },
  "/clinical/prescription": { to: "/prescription", label: "Ordonnances" },
  "/clinical/reporting": { to: "/e-notes", label: "Comptes-rendus" },
  "/clinical/sessions": { to: "/consultation", label: "Séances" },
  "/clinical/therapy": { to: "/consultation", label: "Thérapie" },
  "/clinical/validation": { to: "/lab-results", label: "File de validation" },
  "/clinical/viewer": { to: "/radiology", label: "Visionneuse d'imagerie" },
};

/**
 * The declared modules that are NOT delivered yet.
 *
 * They stay in the manifests — the clinical intent is real and must remain
 * reviewable — but the workspace says so in words instead of linking to a
 * screen that does not exist. The list is explicit so that a module can
 * never fall through unnoticed: a declared module is either in
 * `MODULE_ROUTES` or here, and the test suite asserts it.
 */
export const UNDELIVERED_MODULES: readonly string[] = [
  "/clinical/acuity",
  "/clinical/audiology",
  "/clinical/dialysis",
  "/clinical/ecg",
  "/clinical/endoscopy",
  "/clinical/growth",
  "/clinical/holter",
  "/clinical/lesions",
  "/clinical/monitoring",
  "/clinical/planning",
  "/clinical/pregnancy",
  "/clinical/procedures",
  "/clinical/rehab",
  "/clinical/spirometry",
  "/clinical/surgery",
  "/clinical/transfusion",
  "/clinical/transplant",
  "/clinical/triage",
  "/clinical/vaccines",
  "/clinical/worklist",
];

/**
 * The screen that carries a declared module, or null when the module is not
 * delivered yet.
 */
export function moduleRoute(
  route: string | null | undefined,
): { to: string; label: string } | null {
  if (!route) return null;
  return MODULE_ROUTES[route] ?? null;
}

/**
 * Resolve the workspace route parameter.
 *
 * An unknown identifier never yields a workspace: it yields a 404. A typo in
 * a shared link must be visible immediately — not a blank workspace, and not
 * a silent return to the home page that hides the mistake.
 */
export type SpecialtyRouteResolution =
  | { ok: true; slug: string }
  | { ok: false; reason: "unknown-specialty" };

export function resolveSpecialtyRoute(
  param: string | null | undefined,
): SpecialtyRouteResolution {
  const slug = resolveSpecialtyId(param);
  return slug
    ? { ok: true, slug }
    : { ok: false, reason: "unknown-specialty" };
}

/**
 * The fail-closed decision of the feature flag.
 *
 * The new experience is rendered ONLY when the flag service has answered
 * "enabled". A pending request, an unreachable service, an unknown flag or a
 * malformed answer all leave the clinician on the interface they already
 * know — turning the rollout off must be the default, turning it on the
 * exceptional case.
 */
export function shouldRenderWorkspace(flag: { enabled: boolean }): boolean {
  return flag.enabled === true;
}

/**
 * The specialties that have no act of their own in the NGAP catalogue.
 *
 * Their acts are billed inside another scope. They are real clinical
 * workspaces, and the interface must say so rather than showing an empty
 * catalogue.
 */
export const CODE_ONLY_SPECIALTIES: readonly SpecialtyDefinition[] =
  SPECIALTIES.filter((s) => s.ngap.catalogueActs === 0);
